import { assets } from '@/types/assets.types';

interface FileFolders {
    [key: string]: string[] | FileFolders;
}

type Files = typeof assets & FileFolders;

type FolderKeys = keyof Files;

type RemoveSVG<T extends string> = T extends `${'svg/'}${infer Base}.${'svg'}`
    ? Base
    : T;

type RemoveExtension<T extends string> = T extends `${infer Base}.${string}`
    ? Base
    : T;

type RecursivelyResolveKeys<
    T,
    Prefix,
    Splitter extends string
> = T extends readonly string[]
    ? `${Prefix & string}${Splitter}${T[number]}`
    : T extends object
        ? {
                [K in keyof T]: `${Prefix & string}${Splitter}${RecursivelyResolveKeys<T[K], K, Splitter>}`;
            }[keyof T]
        : never;

type GetIconsByFolder<
    Folder extends keyof Files,
    Splitter extends string
> = RecursivelyResolveKeys<Files[Folder], Folder, Splitter>;

type ExcludeNameKeys<Folder extends keyof Files, Splitter extends string> = {
    [K in Exclude<FolderKeys, Folder>]: Splitter extends `${'/'}`
        ? RemoveExtension<GetIconsByFolder<K, Splitter>>
        : GetIconsByFolder<K, Splitter>;
}[Exclude<FolderKeys, Folder>];

export type ImageNameKeysSlash = ExcludeNameKeys<'svg', '/'>;
export type ImageNameKeys = ImageNameKeysSlash;

export type SvgNameKeysSlash = RemoveSVG<GetIconsByFolder<'svg', '/'>>;
// export type SvgNameKeysDot = GetIconsByFolder<'svg', '/'>;
export type SvgNameKeys = SvgNameKeysSlash;

export function getByPathAssets(input: string) {
    const segments = input.replace(/\./g, '/').split('/').filter(Boolean);

    const searchFiles = (
        currentPath: FileFolders | string[],
        pathAccumulator: string[] = []
    ): string | null => {
        if (Array.isArray(currentPath)) {
            const filename = segments[segments.length - 1];
            if (!filename) return null;

            // Найти файл с точным совпадением имени (с расширением)
            const file = currentPath.find(el => RemoveExtension(el) === filename);
            if (file) {
                return `/assets/images/${pathAccumulator.join('/')}/${file}`; // Возвращаем путь с расширением
            }
        }
        else {
            const entries = Object.entries(currentPath as Files);
            for (const [key, value] of entries) {
                const result = searchFiles(
                    value,
                    pathAccumulator.concat(key)
                );
                if (result) return result;
            }
        }
        return null;
    };

    const getPath = (
        folders: FileFolders,
        remainingSegments: string[]
    ): string | null => {
        let currentFolders: FileFolders | string[] = folders;
        const currentPath: string[] = [];

        while (remainingSegments.length) {
            const segment = remainingSegments.shift() as string;
            if (currentFolders[segment]) {
                currentFolders = currentFolders[segment] as Files;
                currentPath.push(segment);
            }
            else {
                remainingSegments.unshift(segment);
                const deepSearchResult = searchFiles(
                    currentFolders,
                    currentPath
                );
                if (deepSearchResult) return deepSearchResult;
                return null;
            }
        }
        return searchFiles(currentFolders, currentPath);
    };

    return getPath(assets as Files, segments);
}

function RemoveExtension<T extends string>(file: T): string {
    return file.split('.').slice(0, -1).join('.');
}
